import React from 'react';
import styled from 'styled-components';
import Card from '../Components/Card';
import TrendAnalytics from '../Components/charts/TrendAnalytics';
import DonutActivity from '../Components/charts/DonutActivity';
import MetricCircularCard from '../Components/charts/MetricCircularCard';
import useAxios from '../../useAxios';
import { formatDate, getStoredDateFormat } from '../../utils/dateUtils';
import { filterByAccessType, getAccessType } from '../../utils/accessTypeUtils';
import { TrendingUp, Download, Image, Share2, Star, Facebook, RefreshCw, BarChart2, AlertCircle, CheckCircle, XCircle, Video } from 'react-feather';
import ExcelIcon from '../Components/ExcelIcon';

const AnalyticsContainer = styled.div``;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.accentPurple};
    border-color: ${({ theme }) => theme.colors.accentPurple};
  }
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
`;

const LargeChartCard = styled(Card)`
  grid-column: span 8;
  @media (max-width: 1200px) {
    grid-column: span 12;
  }
`;

const SmallChartCard = styled(Card)`
  grid-column: span 4;
  @media (max-width: 1200px) {
    grid-column: span 12;
  }
`;

const FullWidthCard = styled(Card)`
  grid-column: span 12;
`;

const KPIBox = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 32px;
  grid-column: span 12;

  @media (max-width: 1400px) {
    gap: 10px;
  }
  
  @media (max-width: 1024px) {
    gap: 6px;
  }
`;

const KPICard = styled(Card)`
  padding: 20px;
  background-color: ${({ $bgColor }) => $bgColor || '#FFF'};
  border: none;
  box-shadow: none;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 140px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    padding: 10px;
    min-height: 110px;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

const KPITop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const KPIIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.7);
`;

const KPILabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);

  @media (max-width: 1024px) {
    font-size: 11px;
  }
`;

const KPIContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const KPIMain = styled.div``;

const KPIValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #0F0F0F;
  margin-bottom: 4px;

  @media (max-width: 1024px) {
    font-size: 16px;
  }
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(5px);
`;

const AlertModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 450px;
  padding: 32px;
  border-radius: 32px;
  position: relative;
  text-align: center;
`;

const AlertIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => {
    if (props.$type === 'success') return '#10B98120';
    if (props.$type === 'error') return '#EF444420';
    return '#F59E0B20';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: ${props => {
    if (props.$type === 'success') return '#10B981';
    if (props.$type === 'error') return '#EF4444';
    return '#F59E0B';
  }};
`;

const AlertMessage = styled.div`
  font-size: 16px;
  color: #0F0F0F;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const ModalActionFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

// Configuration Constants (can be fetched from API or settings in future)
const ANALYTICS_CONFIG = {
  timeRangeOptions: [1, 7, 30, 90, 0], // added 0 for "All Time"
  defaultTimeRange: 30,
  sessionCalculation: {
    avgTimePerPhoto: 150, // seconds (2.5 minutes)
    avgTimePerShare: 60   // seconds (1 minute)
  },
  topCategoriesLimit: 4,
  platforms: [
    { name: 'WhatsApp', key: 'WhatsApp', color: '#4ADE80', bgColor: '#D1FAE5', icon: 'Share2', growthKey: 'platformGrowth' },
    { name: 'Facebook', key: 'Facebook', color: '#60A5FA', bgColor: '#DBEAFE', icon: 'Facebook', growthKey: 'platformGrowth' },
    { name: 'X', key: 'X', color: '#1A1A1A', bgColor: '#F3F4F6', icon: 'X', growthKey: 'platformGrowth' }, // Fixed key to 'X' from 'Twitter'
    { name: 'Instagram', key: 'Instagram', color: '#F472B6', bgColor: '#FCE7F3', icon: 'Image', growthKey: 'platformGrowth' },
    { name: 'Download', key: 'Direct', color: '#A78BFA', bgColor: '#EDE9FE', icon: 'Download', growthKey: 'downloadGrowth' }
  ],
  kpiCards: [
    { label: 'Total Photos', icon: 'Image', bgColor: '#FEF3C7', trendColor: '#F59E0B' },
    { label: 'Total Videos', icon: 'Video', bgColor: '#D1FAE5', trendColor: '#10B981' },
    { label: 'Total Shares', icon: 'Share2', bgColor: '#E8D5FF', trendColor: '#8B5CF6' },
    { label: 'Unique Customers', icon: 'Star', bgColor: '#FED7AA', trendColor: '#F97316' },
    { label: 'Instagram Page Views', icon: 'Image', bgColor: '#FCE7F3', trendColor: '#E4405F' }
  ]
};

// Custom Icons
const XSocialIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.901 3H22.124L15.086 11.044L23.366 22H16.88L11.801 15.358L5.989 22H2.764L10.292 13.395L2.39999 3H9.04999L13.639 9.066L18.901 3ZM17.77 20.076H19.556L8.083 4.829H6.167L17.77 20.076Z" fill={color} />
  </svg>
);

// Icon mapping
const iconComponents = {
  Image,
  Video,
  Share2,
  TrendingUp,
  Star,
  Facebook,
  Download,
  X: XSocialIcon
};

const Analytics = () => {
  // 1. State Management
  const [timeRange, setTimeRange] = React.useState(ANALYTICS_CONFIG.defaultTimeRange);

  // Alert Modal State
  const [alertModal, setAlertModal] = React.useState({ show: false, message: '', type: 'info' });

  // Helper Functions for Alerts
  const showAlert = (message, type = 'info') => {
    setAlertModal({ show: true, message, type });
  };
  const [loading, setLoading] = React.useState(true);
  const [analyticsData, setAnalyticsData] = React.useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const axiosData = useAxios();

  // State to store API metrics for unique customers
  const [uniqueCustomersGrowth, setUniqueCustomersGrowth] = React.useState(0);

  // Helper function to generate SVG trend path based on growth value
  const generateTrendPath = (growth) => {
    const growthValue = parseFloat(growth) || 0;

    // Normalize growth to a 0-100 scale for better visualization
    const normalizedGrowth = Math.max(-50, Math.min(50, growthValue));
    const scaleFactor = normalizedGrowth / 50; // -1 to 1

    // Calculate Y positions (lower Y = higher on screen)
    const startY = 35; // Middle baseline
    const endYOffset = -scaleFactor * 20; // Move up/down based on growth
    const endY = startY + endYOffset;

    // Create smooth curve points
    const midY = startY + (endYOffset * 0.3);

    const path = `M10,${startY} C25,${startY - scaleFactor * 3} 35,${midY} 50,${midY + scaleFactor * 5} S80,${endY + 5} 90,${endY}`;

    return {
      points: path,
      endX: 85,
      endY: Math.round(endY)
    };
  };

  // Fetch unique customers growth from API
  React.useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const metricsResponse = await axiosData.get(`upload/dashboard-metrics?adminid=${user._id || user.id}`);
        if (metricsResponse.data.metrics?.shareTracking?.uniqueUsers?.growth) {
          setUniqueCustomersGrowth(metricsResponse.data.metrics.shareTracking.uniqueUsers.growth);
        }
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };
    fetchMetrics();
  }, []);

  // 3. Process Logic
  const processAnalytics = React.useCallback((data, growthValue = 0, range = timeRange) => {
    const now = new Date();
    const currentPeriodStart = new Date();

    // For 1 day, show only today (from 00:00:00 today to now)
    // For other ranges, show last N days
    if (range === 1) {
      currentPeriodStart.setHours(0, 0, 0, 0); // Start of today
    } else {
      currentPeriodStart.setDate(now.getDate() - range);
    }

    const previousPeriodStart = new Date(currentPeriodStart);
    let currentData, previousData;

    if (range === 1) {
      // Previous period for 1 day = yesterday (00:00:00 to 23:59:59)
      previousPeriodStart.setDate(previousPeriodStart.getDate() - 1);
      previousPeriodStart.setHours(0, 0, 0, 0);
      const previousPeriodEnd = new Date(previousPeriodStart);
      previousPeriodEnd.setHours(23, 59, 59, 999);

      // Current period: today (00:00 to now)
      const currentPeriodEnd = now;

      // -- Filter Data by Date Ranges
      currentData = data.filter(item => {
        const d = new Date(item.date || item.createdAt);
        return d >= currentPeriodStart && d <= currentPeriodEnd;
      });

      previousData = data.filter(item => {
        const d = new Date(item.date || item.createdAt);
        return d >= previousPeriodStart && d <= previousPeriodEnd;
      });
    } else if (range !== 0) {
      previousPeriodStart.setDate(previousPeriodStart.getDate() - range);

      // -- Filter Data by Date Ranges
      currentData = data.filter(item => {
        const d = new Date(item.date || item.createdAt);
        return d >= currentPeriodStart && d <= now;
      });

      previousData = data.filter(item => {
        const d = new Date(item.date || item.createdAt);
        return d >= previousPeriodStart && d < currentPeriodStart;
      });
    } else {
      // range 0 = All Time
      currentData = data;
      previousData = []; // No previous period for all time
    }

    // -- Constants & Helpers
    const getShareCount = (item) =>
      (item.whatsappsharecount || 0) + (item.facebooksharecount || 0) +
      (item.twittersharecount || 0) + (item.instagramsharecount || 0);

    // const getDownloadCount = (item) => (item.downloadcount || 0);

    const getUniqueCustomers = (dataset) => {
      const uniqueMobiles = new Set();
      dataset.forEach(item => {
        // Use whatsapp as the key, ensure it's a string
        const whatsapp = item.whatsapp ? String(item.whatsapp) : null;
        // Only count valid whatsapp numbers (not empty, not 'N/A', not undefined)
        if (whatsapp && whatsapp !== 'N/A' && whatsapp.trim() !== '') {
          uniqueMobiles.add(whatsapp.trim());
        }
      });
      return uniqueMobiles.size;
    };

    const calculateConversion = (dataset) => {
      if (dataset.length === 0) return 0;
      const sharedPhotos = dataset.filter(item => getShareCount(item) > 0).length;
      return (sharedPhotos / dataset.length * 100).toFixed(1);
    };

    const calculateGrowth = (current, previous) => {
      // Calculate the count change
      const countChange = current - previous;
      // Return the count change directly as percentage (count change = percentage value)
      // If change is +2, show 2%; if change is -5, show -5%
      return parseFloat(countChange.toFixed(1));
    };

    // Calculate average session duration from actual data
    const calculateAvgSessionDuration = (dataset) => {
      if (dataset.length === 0) return { seconds: 0, display: '0s' };

      // Estimate session time based on activity using config values
      const { avgTimePerPhoto, avgTimePerShare } = ANALYTICS_CONFIG.sessionCalculation;

      const totalPhotos = dataset.length;
      const totalShares = dataset.reduce((acc, item) => acc + getShareCount(item), 0);
      const avgSeconds = Math.round((totalPhotos * avgTimePerPhoto + totalShares * avgTimePerShare) / totalPhotos);

      const minutes = Math.floor(avgSeconds / 60);
      const seconds = avgSeconds % 60;
      const display = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

      return { seconds: avgSeconds, display };
    };

    // Separate photos and videos based on accessType (future-proof)
    // Note: templateAccessTypeMap is passed via enrichedData._templateAccessTypeMap
    const templateAccessTypeMap = currentData[0]?._templateAccessTypeMap || {};
    const currentPhotos = filterByAccessType(currentData, 'photomerge', templateAccessTypeMap);
    const currentVideos = filterByAccessType(currentData, 'videomerge', templateAccessTypeMap);
    const previousPhotos = filterByAccessType(previousData, 'photomerge', templateAccessTypeMap);
    const previousVideos = filterByAccessType(previousData, 'videomerge', templateAccessTypeMap);

    // --- KPI Calculations (using photos only for "Total Photos" KPI)
    const stats = {
      photos: {
        current: currentPhotos.length,
        previous: previousPhotos.length
      },
      videos: {
        current: currentVideos.length,
        previous: previousVideos.length
      },
      shares: {
        current: currentData.reduce((acc, item) => acc + getShareCount(item), 0),
        previous: previousData.reduce((acc, item) => acc + getShareCount(item), 0)
      },
      conversion: {
        current: calculateConversion(currentData),
        previous: calculateConversion(previousData)
      },
      session: {
        current: calculateAvgSessionDuration(currentData),
        previous: calculateAvgSessionDuration(previousData)
      },
      instagramClicks: {
        current: currentData.reduce((acc, item) => acc + (item.instagrampageclickcount || 0), 0),
        previous: previousData.reduce((acc, item) => acc + (item.instagrampageclickcount || 0), 0)
      }
    };

    // --- 4. Trend Data (Daily)
    const trends = {};

    // Initialize trends with local dates to avoid timezone shifts
    // If all time, show only last 30 days of trends to avoid overwhelming the chart
    const trendRange = range || 30;
    for (let i = trendRange - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const dayNum = String(d.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${dayNum}`;
      trends[dateKey] = { photos: 0, shares: 0, users: new Set() };
    }

    // For All Time, we still use full currentData for stats, but trends show recent history
    const trendStartDate = new Date(now);
    trendStartDate.setDate(now.getDate() - trendRange);

    currentData.forEach(item => {
      let dateKey;
      try {
        const dateObj = new Date(item.date || item.createdAt);
        if (isNaN(dateObj.getTime())) return;

        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dayNum = String(dateObj.getDate()).padStart(2, '0');
        dateKey = `${year}-${month}-${dayNum}`;
      } catch (e) {
        return;
      }

      if (trends[dateKey]) {
        trends[dateKey].photos += 1;
        trends[dateKey].shares += getShareCount(item);
        // Only add valid whatsapp numbers to unique users count (as string)
        const whatsapp = item.whatsapp ? String(item.whatsapp) : null;
        if (whatsapp && whatsapp !== 'N/A' && whatsapp.trim() !== '') {
          trends[dateKey].users.add(whatsapp.trim());
        }
      }
    });

    // Convert trends sets to counts
    Object.keys(trends).forEach(k => trends[k].uniqueCustomers = trends[k].users.size);

    // --- 5. Category Distribution - Dynamic top 4 templates + Others (matching backend logic)
    const templateCounts = {};
    currentData.forEach(item => {
      const templateName = item.template_name || item.templatename || item.type || "Others";
      templateCounts[templateName] = (templateCounts[templateName] || 0) + 1;
    });

    // Sort templates by count and get top N (from config)
    const topCategoriesLimit = ANALYTICS_CONFIG.topCategoriesLimit;
    const sortedTemplates = Object.entries(templateCounts)
      .filter(([name]) => name !== "Others")
      .sort((a, b) => b[1] - a[1]);

    const topTemplates = sortedTemplates.slice(0, topCategoriesLimit);
    const remainingTemplates = sortedTemplates.slice(topCategoriesLimit);

    // Build categories object with top N + Others
    const categories = {};
    topTemplates.forEach(([name, count]) => {
      categories[name] = count;
    });

    // Sum all remaining templates as "Others"
    const othersCount = remainingTemplates.reduce((sum, [, count]) => sum + count, 0) + (templateCounts["Others"] || 0);
    if (othersCount > 0) {
      categories["Others"] = othersCount;
    }

    // --- 6. Platform Performance (Dynamic - based on available platforms)
    // Dynamically detect which platforms are used in the data
    const platformKeys = ['WhatsApp', 'Facebook', 'X', 'Instagram'];
    const platforms = {};
    const downloads = { Direct: 0 };

    // Initialize platforms dynamically
    platformKeys.forEach(key => {
      platforms[key] = 0;
    });

    currentData.forEach(item => {
      if (item.whatsappsharecount) platforms.WhatsApp = (platforms.WhatsApp || 0) + (item.whatsappsharecount || 0);
      if (item.facebooksharecount) platforms.Facebook = (platforms.Facebook || 0) + (item.facebooksharecount || 0);
      if (item.twittersharecount) platforms.X = (platforms.X || 0) + (item.twittersharecount || 0);
      if (item.instagramsharecount) platforms.Instagram = (platforms.Instagram || 0) + (item.instagramsharecount || 0);
      if (item.downloadcount) downloads.Direct += (item.downloadcount || 0);
    });

    // Calculate platform growth (current vs previous period)
    const previousPlatforms = {};
    const previousDownloads = { Direct: 0 };

    // Initialize previous platforms dynamically
    platformKeys.forEach(key => {
      previousPlatforms[key] = 0;
    });

    previousData.forEach(item => {
      if (item.whatsappsharecount) previousPlatforms.WhatsApp = (previousPlatforms.WhatsApp || 0) + (item.whatsappsharecount || 0);
      if (item.facebooksharecount) previousPlatforms.Facebook = (previousPlatforms.Facebook || 0) + (item.facebooksharecount || 0);
      if (item.twittersharecount) previousPlatforms.X = (previousPlatforms.X || 0) + (item.twittersharecount || 0);
      if (item.instagramsharecount) previousPlatforms.Instagram = (previousPlatforms.Instagram || 0) + (item.instagramsharecount || 0);
      if (item.downloadcount) previousDownloads.Direct += (item.downloadcount || 0);
    });

    const platformGrowth = {};
    Object.keys(platforms).forEach(key => {
      platformGrowth[key] = calculateGrowth(platforms[key], previousPlatforms[key]);
    });

    const downloadGrowth = {};
    Object.keys(downloads).forEach(key => {
      downloadGrowth[key] = calculateGrowth(downloads[key], previousDownloads[key]);
    });

    // Total Engagement (Shares + Downloads combined for display)
    const totalEngagement = {
      WhatsApp: platforms.WhatsApp,
      Facebook: platforms.Facebook,
      X: platforms.X,
      Instagram: platforms.Instagram,
      Direct: downloads.Direct
    };

    // --- 7. Peak Hours & Day Analysis
    const peakHours = new Array(24).fill(0);
    const dayPerformance = {}; // { 'Monday': { photos: 0 } }

    currentData.forEach(item => {
      const d = new Date(item.date || item.createdAt);
      const hour = d.getHours();
      const day = d.toLocaleDateString('en-US', { weekday: 'long' });

      peakHours[hour]++;

      if (!dayPerformance[day]) dayPerformance[day] = 0;
      dayPerformance[day]++;
    });

    // --- 8. Generated Insights
    const bestHourIndex = peakHours.indexOf(Math.max(...peakHours));
    const bestCategory = Object.entries(categories).sort((a, b) => b[1] - a[1])[0];
    const bestDay = Object.entries(dayPerformance).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // Growth Logic (Mocked logic for specific comparison)
    const weekendCount = (dayPerformance['Saturday'] || 0) + (dayPerformance['Sunday'] || 0);
    const weekdayCount = Object.values(dayPerformance).reduce((a, b) => a + b, 0) - weekendCount;
    const weekendGrowth = weekendCount > weekdayCount ? "High Weekend Traffic" : "Steady Weekday Traffic";

    const fastestPlatform = (() => {
      // Dynamic: which platform has most shares
      const sorted = Object.entries(platforms).filter(([_, count]) => count > 0).sort((a, b) => b[1] - a[1]);
      return sorted[0]?.[0] || (platformKeys[0] || 'WhatsApp');
    })();

    // Calculate unique customers for current period (growth comes from API)
    const uniqueCustomersCurrent = getUniqueCustomers(currentData);

    const sessionGrowth = calculateGrowth(stats.session.current.seconds, stats.session.previous.seconds);

    setAnalyticsData({
      kpis: {
        photos: {
          value: stats.photos.current,
          growth: calculateGrowth(stats.photos.current, stats.photos.previous)
        },
        videos: {
          value: stats.videos.current,
          growth: calculateGrowth(stats.videos.current, stats.videos.previous)
        },
        shares: {
          value: stats.shares.current,
          growth: calculateGrowth(stats.shares.current, stats.shares.previous)
        },
        conversion: {
          value: stats.conversion.current,
          growth: calculateGrowth(parseFloat(stats.conversion.current), parseFloat(stats.conversion.previous))
        },
        session: {
          value: stats.session.current.display,
          growth: sessionGrowth
        },
        instagramClicks: {
          value: stats.instagramClicks.current,
          growth: calculateGrowth(stats.instagramClicks.current, stats.instagramClicks.previous)
        }
      },
      uniqueCustomers: uniqueCustomersCurrent,
      uniqueCustomersGrowth: growthValue, // From API
      trends,
      categories,
      platforms, // Social shares only
      downloads, // Downloads separate
      totalEngagement, // Combined for display
      platformGrowth, // Add platform growth data
      downloadGrowth, // Download growth
      peakHours,
      insights: {
        bestTimeStr: `${bestHourIndex}:00 - ${bestHourIndex + 1}:00`,
        bestDay,
        bestCategory: bestCategory ? `${bestCategory[0]} (${Math.round(bestCategory[1] / (currentData.length || 1) * 100 || 0)}%)` : 'N/A',
        growthPlatform: fastestPlatform,
        trafficPattern: weekendGrowth,
        recommendation: `Schedule ${fastestPlatform} campaigns on ${bestDay}s at ${bestHourIndex}:00 for max conversion.`
      }
    });
  }, [timeRange]);

  // 2. Fetch Data
  const fetchAnalyticsData = React.useCallback(async () => {
    try {
      setLoading(true);

      // Fetch templates to get accessType mapping
      let templateAccessTypeMap = {};
      try {
        const templatesResponse = await axiosData.get(`photomerge/templates?adminid=${user._id || user.id}`);
        const templates = Array.isArray(templatesResponse.data) ? templatesResponse.data : [];
        templates.forEach(template => {
          if (template.templatename) {
            templateAccessTypeMap[template.templatename] = template.accessType || 'photomerge';
          }
        });
      } catch (templatesError) {
        console.error("Error fetching templates:", templatesError);
      }

      // Optimized: Fetch data in chunks for better performance
      // Start with last 2000 items for initial display, fetch more in background if needed
      const response = await axiosData.get(`upload/all?adminid=${user._id || user.id}&page=1&limit=2000`);
      // Handle both paginated and non-paginated responses
      const dataArray = Array.isArray(response.data?.data)
        ? response.data.data
        : (Array.isArray(response.data) ? response.data : []);
      // Filter to include both photo merge app and video merge app items 
      const rawData = dataArray.filter(item =>
        item.source === 'photo merge app' || item.source === 'video merge app'
      );

      // Add accessType to each item for processing (future-proof)
      const enrichedData = rawData.map(item => {
        const accessType = getAccessType(item, templateAccessTypeMap);
        return {
          ...item,
          _accessType: accessType,
          _templateAccessTypeMap: templateAccessTypeMap // Pass map for filtering
        };
      });

      processAnalytics(enrichedData, uniqueCustomersGrowth, timeRange);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setLoading(false);
    }
  }, [timeRange, axiosData, user._id, user.id, uniqueCustomersGrowth, processAnalytics]);

  React.useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  // Function to generate dynamic tags based on insights
  const getRecommendationTags = () => {
    if (!analyticsData?.insights) return [];

    const tags = [];
    const recommendation = analyticsData.insights.recommendation?.toLowerCase() || '';
    const { growthPlatform, bestDay, trafficPattern } = analyticsData.insights;

    // Campaign Optimization - if recommendation mentions campaigns, scheduling, or platforms
    if (recommendation.includes('campaign') || recommendation.includes('schedule') || growthPlatform) {
      tags.push({
        label: 'Campaign Optimization',
        bgColor: '#DBEAFE',
        textColor: '#3B82F6'
      });
    }

    // Engagement - if recommendation mentions conversion, engagement, or sharing
    if (recommendation.includes('conversion') || recommendation.includes('engagement') || recommendation.includes('share')) {
      tags.push({
        label: 'Engagement',
        bgColor: '#D1FAE5',
        textColor: '#10B981'
      });
    }

    // Timing - if recommendation mentions specific time, day, or peak hours
    if (recommendation.includes('time') || bestDay || recommendation.includes('hour')) {
      tags.push({
        label: 'Timing',
        bgColor: '#FEF3C7',
        textColor: '#D97706'
      });
    }

    // Platform - always show if growthPlatform exists (recommendation always includes it)
    if (growthPlatform) {
      tags.push({
        label: growthPlatform,
        bgColor: '#E8D5FF',
        textColor: '#8B5CF6'
      });
    }

    // Performance - if mentions best category or peak performance
    if (recommendation.includes('max') || recommendation.includes('best') || recommendation.includes('peak')) {
      tags.push({
        label: 'Performance',
        bgColor: '#FBCFE8',
        textColor: '#EC4899'
      });
    }

    // Traffic Pattern - if weekend/weekday pattern is mentioned
    if (trafficPattern && (trafficPattern.includes('Weekend') || trafficPattern.includes('Weekday'))) {
      tags.push({
        label: 'Traffic Pattern',
        bgColor: '#DBEAFE',
        textColor: '#3B82F6'
      });
    }

    // If no tags found, return default tags
    if (tags.length === 0) {
      tags.push(
        { label: 'Campaign Optimization', bgColor: '#DBEAFE', textColor: '#3B82F6' },
        { label: 'Engagement', bgColor: '#D1FAE5', textColor: '#10B981' }
      );
    }

    // Return unique tags (remove duplicates)
    return tags.filter((tag, index, self) =>
      index === self.findIndex(t => t.label === tag.label)
    );
  };

  const handleTimeRangeChange = (days) => {
    setTimeRange(days);
    // Data will automatically refresh via useEffect
  };

  const handleExport = () => {
    if (!analyticsData) {
      showAlert('No data available to export. Please wait for data to load.', 'error');
      return;
    }

    try {
      const timeRangeText = timeRange === 1 ? 'Today' : `Last ${timeRange} Days`;
      const csvRows = [];

      // 1. Report Header
      csvRows.push([`ANALYTICS PERFORMANCE REPORT`]);
      csvRows.push([`Period: ${timeRangeText}`]);
      csvRows.push([`Generated On: ${formatDate(new Date(), getStoredDateFormat() + ' HH:mm')}`]);
      csvRows.push([]); // Spacer

      // 2. KPI Metrics Table
      csvRows.push([`--- SUMMARY METRICS ---`]);
      csvRows.push([`Metric`, `Value`, `Growth (%)`]);
      csvRows.push([`Total Photos`, analyticsData.kpis.photos.value, `${analyticsData.kpis.photos.growth}%`]);
      csvRows.push([`Total Shares`, analyticsData.kpis.shares.value, `${analyticsData.kpis.shares.growth}%`]);
      csvRows.push([`Conversion Rate`, `${analyticsData.kpis.conversion.value}%`, `${analyticsData.kpis.conversion.growth}%`]);
      csvRows.push([`Avg Session Duration`, analyticsData.kpis.session.value, `${analyticsData.kpis.session.growth}%`]);
      csvRows.push([`Unique Customers`, analyticsData.uniqueCustomers, `${analyticsData.uniqueCustomersGrowth}%`]);
      csvRows.push([]); // Spacer

      // 3. Category Distribution Table
      csvRows.push([`--- TOP TEMPLATE CATEGORIES ---`]);
      csvRows.push([`Category`, `Usage Count`, `Percentage (%)`]);
      const totalPhotos = analyticsData.kpis.photos.value;
      Object.entries(analyticsData.categories).forEach(([name, count]) => {
        const percentage = totalPhotos > 0 ? ((count / totalPhotos) * 100).toFixed(1) : 0;
        csvRows.push([name, count, `${percentage}%`]);
      });
      csvRows.push([]); // Spacer

      // 4. Platform Performance Table
      csvRows.push([`--- SOCIAL PLATFORM ENGAGEMENT ---`]);
      csvRows.push([`Platform`, `Total Shares`, `Growth (%)`]);
      Object.entries(analyticsData.platforms || {}).forEach(([name, shares]) => {
        const growth = analyticsData.platformGrowth?.[name] || 0;
        csvRows.push([name, shares, `${growth}%`]);
      });

      // Include Downloads in performance if present
      if (analyticsData.downloads && analyticsData.downloads.Direct > 0) {
        const dGrowth = analyticsData.downloadGrowth?.Direct || 0;
        csvRows.push([`App Downloads`, analyticsData.downloads.Direct, `${dGrowth}%`]);
      }
      csvRows.push([]); // Spacer

      // 5. Daily Trends Table
      csvRows.push([`--- DAILY PERFORMANCE TRENDS ---`]);
      csvRows.push([`Date`, `Photos Created`, `Shares Produced`, `Unique Customers`]);
      // Sort dates for clearer timeline
      const sortedTrendEntries = Object.entries(analyticsData.trends || {}).sort((a, b) => new Date(a[0]) - new Date(b[0]));
      sortedTrendEntries.forEach(([date, data]) => {
        csvRows.push([`\t${date}`, data.photos || 0, data.shares || 0, data.uniqueCustomers || 0]);
      });
      csvRows.push([]); // Spacer

      // 6. Insights & Recommendations
      csvRows.push([`--- DATA INSIGHTS ---`]);
      csvRows.push([`Insight Metric`, `Description / Result`]);
      csvRows.push([`Top Performing Category`, analyticsData.insights.bestCategory]);
      csvRows.push([`Peak Engagement Time`, `${analyticsData.insights.bestDay} at ${analyticsData.insights.bestTimeStr}`]);
      csvRows.push([`Fastest Growing Platform`, analyticsData.insights.growthPlatform]);
      csvRows.push([`Traffic Pattern Observed`, analyticsData.insights.trafficPattern]);
      csvRows.push([`Strategic Recommendation`, analyticsData.insights.recommendation]);

      // Robust CSV conversion with proper escaping
      const csvContent = "\uFEFF" + csvRows.map(row =>
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ).join("\n");

      // Create and download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileNameSuffix = timeRange === 1 ? 'Today' : `${timeRange}Days`;
      a.download = `Analytics_Full_Report_${fileNameSuffix}_${formatDate(new Date(), 'YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting analytics:", error);
      showAlert('Failed to export analytics data. Please try again.', 'error');
    }
  };


  return (
    <AnalyticsContainer>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <HeaderSection>
        <div>
          <h1>Analytics Dashboard</h1>
          <p style={{ color: '#7A7A7A', marginTop: '8px' }}>
            Track performance, engagement, and conversion metrics
          </p>
        </div>

        <ActionButtons>
          <div style={{ display: 'flex', gap: '8px' }}>
            {ANALYTICS_CONFIG.timeRangeOptions.map(days => (
              <ActionButton
                key={days}
                onClick={() => handleTimeRangeChange(days)}
                style={{
                  background: timeRange === days ? '#E0E7FF' : 'transparent',
                  borderColor: timeRange === days ? '#6366F1' : '#E5E7EB',
                  fontWeight: timeRange === days ? 600 : 500
                }}
              >
                {days === 1 ? 'Today' : `${days} Days`}
              </ActionButton>
            ))}
          </div>
          <ActionButton
            onClick={handleRefresh}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'wait' : 'pointer'
            }}
          >
            <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </ActionButton>
          <ActionButton onClick={handleExport} disabled={!analyticsData || loading}>
            <ExcelIcon size={16} />
            Export
          </ActionButton>
        </ActionButtons>
      </HeaderSection>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading analytics...</div>
        // ) : !analyticsData || !analyticsData.kpis?.photos?.value || analyticsData.kpis.photos.value === 0 ? (
      ) : !analyticsData ? (
        <div style={{
          padding: '80px 40px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#F3F4F6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            color: '#999'
          }}>
            <BarChart2 size={40} />
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 800,
            color: '#111827',
            marginBottom: '8px'
          }}>
            No data for this filter
          </div>
          <div style={{
            color: '#6B7280',
            textAlign: 'center',
            maxWidth: '400px',
            fontSize: '15px',
            lineHeight: '1.6',
            marginBottom: '24px'
          }}>
            {timeRange !== ANALYTICS_CONFIG.defaultTimeRange
              ? `We couldn't find any analytics data for the selected time period. Try selecting a different time range.`
              : "No analytics data available. Data will appear here once customers start creating and sharing photos."}
          </div>
          {timeRange !== ANALYTICS_CONFIG.defaultTimeRange && (
            <ActionButton
              style={{ marginTop: '8px', background: '#F3F4F6', color: '#374151', border: 'none' }}
              onClick={() => {
                setTimeRange(ANALYTICS_CONFIG.defaultTimeRange);
              }}
            >
              Reset Filter
            </ActionButton>
          )}
        </div>
      ) : (
        <AnalyticsGrid>
          {/* KPI Cards */}
          <KPIBox>
            {(() => {
              const kpiDataMap = {
                'Total Photos': {
                  value: analyticsData?.kpis?.photos?.value || 0,
                  growth: analyticsData?.kpis?.photos?.growth || 0
                },
                'Total Videos': {
                  value: analyticsData?.kpis?.videos?.value || 0,
                  growth: analyticsData?.kpis?.videos?.growth || 0
                },
                'Total Shares': {
                  value: analyticsData?.kpis?.shares?.value || 0,
                  growth: analyticsData?.kpis?.shares?.growth || 0
                },
                'Unique Customers': {
                  value: analyticsData?.uniqueCustomers || 0,
                  growth: analyticsData?.uniqueCustomersGrowth || 0
                },
                'Instagram Page Views': {
                  value: analyticsData?.kpis?.instagramClicks?.value || 0,
                  growth: analyticsData?.kpis?.instagramClicks?.growth || 0
                }
              };

              return ANALYTICS_CONFIG.kpiCards.map((kpiConfig, idx) => {
                const kpiData = kpiDataMap[kpiConfig.label];
                const trend = generateTrendPath(kpiData?.growth || 0);
                const IconComponent = iconComponents[kpiConfig.icon];

                let displayValue = kpiData?.value;
                if (typeof displayValue === 'number') {
                  displayValue = displayValue.toLocaleString();
                } else {
                  displayValue = displayValue || '0';
                }

                const growthValue = kpiData?.growth || 0;
                const changeDisplay = `${growthValue >= 0 ? '+' : ''}${growthValue}%`;

                return {
                  label: kpiConfig.label,
                  value: displayValue,
                  change: changeDisplay,
                  icon: <IconComponent size={20} />,
                  bgColor: kpiConfig.bgColor,
                  trendColor: kpiConfig.trendColor,
                  points: trend.points,
                  endX: trend.endX,
                  endY: trend.endY
                };
              });
            })().map((kpi, idx) => (
              <KPICard key={idx} $bgColor={kpi.bgColor}>
                <KPITop>
                  <KPIIconWrapper>
                    {kpi.icon}
                  </KPIIconWrapper>
                  <KPILabel>{kpi.label}</KPILabel>
                </KPITop>

                <KPIContent>
                  <KPIMain>
                    <KPIValue>{kpi.value}</KPIValue>
                    <TrendIndicator $color={kpi.trendColor}>
                      ▲ {kpi.change}
                    </TrendIndicator>
                  </KPIMain>

                  <div style={{ position: 'relative', width: '100px', height: '50px' }}>
                    <svg width="100%" height="100%" viewBox="0 0 100 50" style={{ overflow: 'visible' }}>
                      <path
                        d={kpi.points}
                        fill="none"
                        stroke={kpi.trendColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                        style={{ filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.1))' }}
                      />
                      <circle
                        cx={kpi.endX}
                        cy={kpi.endY}
                        r="4"
                        fill="white"
                        stroke={kpi.trendColor}
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </KPIContent>
              </KPICard>
            ))}
          </KPIBox>


          <LargeChartCard title="Performance Trends" subtitle={timeRange === 1 ? 'Daily activity for today' : `Daily activity for last ${timeRange} days`}>
            <div style={{ height: '400px', marginTop: '20px' }}>
              <TrendAnalytics data={analyticsData?.trends} range={timeRange} />
            </div>
          </LargeChartCard>

          <SmallChartCard dark title="Category Distribution" subtitle="Top performing Templates">
            <div style={{ height: '400px', marginTop: '20px' }}>
              <DonutActivity dark data={analyticsData?.categories} />
            </div>
          </SmallChartCard>

          <FullWidthCard title="Platform Performance" subtitle="Shares across channels">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '24px',
              padding: '24px 0'
            }}>
              {(() => {
                const totalEngagement = Object.values(analyticsData?.totalEngagement || {}).reduce((a, b) => a + b, 0) || 1;

                // Dynamically build platform list based on config and data availability
                return ANALYTICS_CONFIG.platforms
                  .map(platform => {
                    const key = platform.key || platform.name;
                    const IconComponent = iconComponents[platform.icon];

                    return (
                      <MetricCircularCard
                        key={key}
                        label={platform.name === "X" ? "Twitter" : platform.name}
                        value={analyticsData?.totalEngagement?.[key] || 0}
                        percentage={Math.round(((analyticsData?.totalEngagement?.[key] || 0) / totalEngagement) * 100)}
                        trend={parseFloat(analyticsData?.[platform.growthKey]?.[key] || 0)}
                        color={platform.color}
                        bgColor={platform.bgColor}
                        icon={<IconComponent size={20} />}
                      />
                    );
                  });
              })()}
            </div>
          </FullWidthCard>

          <FullWidthCard title="Insights & Recommendations" subtitle="AI Derived Actions">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              padding: '20px 0'
            }}>
              <div>
                <h4 style={{ marginBottom: '10px', color: '#1A1A1A' }}>🏆 Top Performance</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Best Category</p>
                <p style={{ fontWeight: 600, color: '#1A1A1A' }}>{analyticsData?.insights.bestCategory}</p>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '12px', marginBottom: '4px' }}>Peak Day & Time</p>
                <p style={{ fontWeight: 600, color: '#1A1A1A' }}>{analyticsData?.insights.bestDay}, {analyticsData?.insights.bestTimeStr}</p>
              </div>
              <div>
                <h4 style={{ marginBottom: '10px', color: '#1A1A1A' }}>🚀 Growth Areas</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Fastest Growing Platform</p>
                <p style={{ fontWeight: 600, color: '#1A1A1A' }}>{analyticsData?.insights.growthPlatform}</p>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '12px', marginBottom: '4px' }}>Traffic Pattern</p>
                <p style={{ fontWeight: 600, color: '#1A1A1A' }}>{analyticsData?.insights.trafficPattern}</p>
              </div>
              <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px' }}>
                <h4 style={{ marginBottom: '10px', color: '#1A1A1A' }}>💡 Smart Recommendation</h4>
                <p style={{ color: '#4B5563', lineHeight: '1.5' }}>{analyticsData?.insights.recommendation}</p>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {getRecommendationTags().map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '4px 12px',
                        background: tag.bgColor,
                        color: tag.textColor,
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </FullWidthCard>
        </AnalyticsGrid>
      )}
      {/* Alert Modal */}
      {alertModal.show && (
        <ModalOverlay onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}>
          <AlertModalContent onClick={e => e.stopPropagation()}>
            <AlertIconWrapper $type={alertModal.type}>
              {alertModal.type === 'success' ? (
                <CheckCircle size={32} />
              ) : alertModal.type === 'error' ? (
                <XCircle size={32} />
              ) : (
                <AlertCircle size={32} />
              )}
            </AlertIconWrapper>
            <AlertMessage>{alertModal.message}</AlertMessage>
            <ModalActionFooter>
              <ActionButton
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
              >
                OK
              </ActionButton>
            </ModalActionFooter>
          </AlertModalContent>
        </ModalOverlay>
      )}
    </AnalyticsContainer>
  );
};

export default Analytics;
